import React, { useState, useMemo, useEffect, useRef } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown, faChevronUp, faCheck } from '@fortawesome/free-solid-svg-icons'
import MultiRangeSlider from '../components/MultiRangeSlider/MultiRangeSlider'
import { sprzedawcyDlaKraju } from '../sprzedawcyKrajow'
import { fetchSeriesOptions } from '../api/fetchProducts'
import { useTranslation } from 'react-i18next'
import { KRAJ } from '../kraj'
import { formatPrice } from '../utils'

// Lista sklepów kraju wersji serwisu. Dla Polski to te same obiekty co
// panelSellers/inverterSellers/magazineSellers z filtersSchema.js.
const getSellerCatalog = (productType) => sprzedawcyDlaKraju(productType, KRAJ.kod)

// ===== Parametry elektryczne (dane z kart katalogowych producentów) =====
// Zakresy od-do: kto filtruje po Vmp czy prądzie MPPT, ma już w głowie konkretną
// wartość albo widełki — presety by tu tylko przeszkadzały (inaczej niż przy
// sprawności, gdzie naturalne pytanie brzmi „co najmniej X%").
// Klucz -> nazwy parametrów API (min_*/max_*), zgodne ze spec/filtering.py backendu.
// Nazwa parametru na ekranie: lista:filtry.elektryczne.<key>.
const ELEC_RANGES_PANEL = [
  { key: 'vmp', unit: 'V', minParam: 'min_vmp', maxParam: 'max_vmp' },
  { key: 'imp', unit: 'A', minParam: 'min_imp', maxParam: 'max_imp' },
  { key: 'voc', unit: 'V', minParam: 'min_voc', maxParam: 'max_voc' },
  { key: 'isc', unit: 'A', minParam: 'min_isc', maxParam: 'max_isc' },
]
const ELEC_RANGES_INVERTER = [
  { key: 'ac_current', unit: 'A', minParam: 'min_ac_current', maxParam: 'max_ac_current' },
  { key: 'mppt_current', unit: 'A', minParam: 'min_mppt_current', maxParam: 'max_mppt_current' },
  { key: 'mppt_isc', unit: 'A', minParam: 'min_mppt_isc', maxParam: 'max_mppt_isc' },
  { key: 'dc_voltage', unit: 'V', minParam: 'min_dc_voltage', maxParam: 'max_dc_voltage' },
]
// Magazyny: karty LV opisują zdolność PRĄDEM, a HV MOCĄ (Deye SE-F16 podaje
// 160/230 A, GoodWe Lynx LX D — 3/5 kW), więc obie formy muszą być do wyboru.
// Pojemności nominalnej tu nie ma: filtruje ją suwak w sekcji głównej.
const ELEC_RANGES_MAGAZINE = [
  { key: 'mag_usable', unit: 'kWh', minParam: 'min_usable_energy', maxParam: 'max_usable_energy' },
  { key: 'mag_v_nom', unit: 'V', minParam: 'min_voltage_nominal', maxParam: 'max_voltage_nominal' },
  { key: 'mag_ah', unit: 'Ah', minParam: 'min_capacity_ah', maxParam: 'max_capacity_ah' },
  { key: 'mag_i_chg', unit: 'A', minParam: 'min_charge_current', maxParam: 'max_charge_current' },
  { key: 'mag_i_dis', unit: 'A', minParam: 'min_discharge_current', maxParam: 'max_discharge_current' },
  { key: 'mag_p_chg', unit: 'kW', minParam: 'min_charge_power', maxParam: 'max_charge_power' },
  { key: 'mag_p_dis', unit: 'kW', minParam: 'min_discharge_power', maxParam: 'max_discharge_power' },
]
const ALL_ELEC_RANGES = [...ELEC_RANGES_PANEL, ...ELEC_RANGES_INVERTER, ...ELEC_RANGES_MAGAZINE]

// Liczba cykli — progi z realnego katalogu (spec_magazines): w danych występują
// wyłącznie 4000, 6000 i 8000, więc opcje pokrywają dokładnie te trzy poziomy.
// Etykiety opcji liczone przy renderze (t z lista:filtry.opcje), bo zależą od języka.
const opcje = (wartosci, etykieta) => wartosci.map((value) => ({ label: etykieta(value), value }))
// 98 -> „98", 98.5 -> „98,5" (separator dziesiętny z locale kraju).
const procent = (v) => formatPrice(v, Number.isInteger(v) ? 0 : 1)
// Współczynniki temperaturowe ze znakiem minus U+2212 i dwoma miejscami: „−0,30".
const wspolczynnik = (v) => `\u2212${formatPrice(Math.abs(v), 2)}`

const ADVANCED_MAG_CYCLE_OPTS = (t) =>
  opcje([8000, 6000, 4000], (v) => t('filtry.opcje.od_cykli', { count: v }))
// Klasy szczelności obecne w danych. IP20/IP21 to sprzęt WYŁĄCZNIE do wnętrz,
// IP54 i wyżej wychodzi na zewnątrz — stąd kolejność od najszczelniejszych.
const MAG_IP_OPTIONS = ['IP66', 'IP65', 'IP55', 'IP54', 'IP21', 'IP20']
const ADVANCED_MAG_DOD_OPTS = (t) =>
  opcje([95, 90, 80], (v) => t('filtry.opcje.od_procent', { wartosc: procent(v) }))
// Ile modułów da się spiąć równolegle — decyduje o tym, czy magazyn urośnie
// razem z instalacją, czy trzeba go będzie wymienić.
const ADVANCED_MAG_PARALLEL_OPTS = (t) =>
  opcje([10, 6, 4], (v) => t('filtry.opcje.od_modulow', { count: v }))

const emptyElecRanges = () =>
  ALL_ELEC_RANGES.reduce((acc, r) => { acc[r.key] = { min: '', max: '' }; return acc }, {})

// Liczba ogniw: wartości z realnego katalogu (spec_panels), nie z przeczucia.
// Dominują panele half-cut: 108 (standard domowy), 120, 132, 144 (komercyjne);
// 54/60/66/72/96 to starsze i pełnowymiarowe konstrukcje.
const CELLS_OPTIONS = [54, 60, 66, 72, 96, 108, 120, 132, 144]

// Współczynniki temperaturowe są ujemne — „lepszy" znaczy bliższy zera, stąd
// opcje czytane jako „X lub lepszy". Progi z rozkładu danych: γ skupia się przy
// −0,29 (nowoczesny TOPCon), −0,26 wyłuskuje premium (HJT), −0,30 odcina
// starsze konstrukcje. αIsc nie ma filtra: w danych ma 2 wartości na 88 modeli.
const ADVANCED_GAMMA_OPTS = (t) =>
  opcje([-0.26, -0.29, -0.30], (v) => t('filtry.opcje.wsp_lub_lepszy', { wartosc: wspolczynnik(v) }))
const ADVANCED_BETA_OPTS = (t) =>
  opcje([-0.23, -0.25, -0.27], (v) => t('filtry.opcje.wsp_lub_lepszy', { wartosc: wspolczynnik(v) }))
const ADVANCED_INV_EFF_MAX_OPTS = (t) =>
  opcje([98.5, 98, 97.5], (v) => t('filtry.opcje.powyzej_procent', { wartosc: procent(v) }))
const ADVANCED_INV_EFF_EURO_OPTS = (t) =>
  opcje([98, 97.5, 97], (v) => t('filtry.opcje.powyzej_procent', { wartosc: procent(v) }))

// Własny checkbox — spójny wygląd w Chrome i Edge (appearance-none + własna fajka).
const StyledCheckbox = ({ checked, onChange, name, value }) => (
  <span className='relative inline-flex items-center justify-center flex-shrink-0 w-4 h-4'>
    <input
      type='checkbox'
      name={name}
      value={value}
      checked={checked}
      onChange={onChange}
      className='peer appearance-none w-4 h-4 border border-gray-300 rounded bg-white checked:bg-blue-600 checked:border-blue-600 hover:border-blue-400 cursor-pointer transition-colors'
    />
    <FontAwesomeIcon
      icon={faCheck}
      className='pointer-events-none absolute text-white text-[9px] opacity-0 peer-checked:opacity-100'
    />
  </span>
)

// ----- Collapsible preset group (single-select, options hidden until expanded) -----
// `wariant='glowny'` — filtr stoi w sekcji GŁÓWNEJ, więc wygląda DOKŁADNIE jak
// Producent czy Rodzaj: ten sam `SectionHeader`, ta sama ramka rozdzielająca,
// ten sam odstęp treści. Wcześniej te trzy filtry (Sprawność, Gwarancja, Cena za Wp)
// miały własny, ciaśniejszy rytm — stały w jednym wspólnym pudełku po `mb-2`,
// podczas gdy grupy checkboxów dostają każda swoje `pb-4 mb-4`. Przy przewijaniu
// lewej kolumny było widać, że to dwa różne komponenty, a nie jedna lista filtrów.
const CollapsiblePresetGroup = ({
  groupKey,
  label,
  options,
  selected,
  onSelect,
  isOpen,
  onToggle,
  selectedLabel,
  wariant = 'zaawansowany',
}) => {
  const activeLabel = selectedLabel ?? options.find(o => o.value === selected)?.label ?? null
  const glowny = wariant === 'glowny'

  return (
  <div className={glowny
    ? 'border-b border-gray-100 pb-4 mb-4'
    : 'mb-1 border-b border-gray-50 last:border-0'}>
    {glowny ? (
      <SectionHeader
        title={label}
        isOpen={isOpen}
        onToggle={() => onToggle(groupKey)}
        badge={activeLabel}
      />
    ) : (
      <button
        type='button'
        onClick={() => onToggle(groupKey)}
        className='w-full flex items-center justify-between gap-2 rounded-md transition-colors
                   text-left px-1 py-2.5 hover:bg-gray-50'
      >
        <span className='text-[11px] font-semibold text-gray-500 uppercase tracking-wider'>
          {label}
        </span>
        <span className='flex items-center gap-2 min-w-0'>
          {activeLabel && (
            <span className='text-[10px] text-blue-600 font-medium truncate max-w-[7rem]'>
              {activeLabel}
            </span>
          )}
          <FontAwesomeIcon
            icon={isOpen ? faChevronUp : faChevronDown}
            className='flex-shrink-0 text-[10px] text-gray-300'
          />
        </span>
      </button>
    )}
    {isOpen && (
      <div className={glowny ? 'mt-3 px-1 space-y-2' : 'space-y-1 pb-3 px-1'}>
        {options.map(opt => (
          <button
            key={opt.value ?? opt.label}
            type='button'
            onClick={() => onSelect(selected === opt.value ? null : opt.value)}
            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
              selected === opt.value
                ? 'bg-blue-50 text-blue-700 border border-blue-200 font-medium'
                : 'text-gray-600 hover:bg-gray-50 border border-transparent'
            }`}
          >
            {selected === opt.value && (
              <span className='mr-1.5 text-blue-500'>✓</span>
            )}
            {opt.label}
          </button>
        ))}
      </div>
    )}
  </div>
  )
}

// ----- Collapsible range group (od–do inputs, same chrome as preset groups) -----
const CollapsibleRangeGroup = ({ groupKey, label, unit, value, onChange, isOpen, onToggle }) => {
  const { t } = useTranslation('lista')
  const summary = (value?.min !== '' || value?.max !== '')
    ? `${value.min !== '' ? value.min : '…'}–${value.max !== '' ? value.max : '…'} ${unit}`
    : null

  const inputCls = 'px-2 py-1.5 border border-gray-200 rounded-lg w-[5.5rem] text-center pr-6 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400'

  return (
    <div className='mb-1 border-b border-gray-50 last:border-0'>
      <button
        type='button'
        onClick={() => onToggle(groupKey)}
        className='w-full flex items-center justify-between gap-2 px-1 py-2.5 rounded-md hover:bg-gray-50 transition-colors text-left'
      >
        <span className='text-[11px] font-semibold text-gray-500 uppercase tracking-wider'>
          {label}
        </span>
        <span className='flex items-center gap-2 min-w-0'>
          {summary && (
            <span className='text-[10px] text-blue-600 font-medium truncate max-w-[7rem]'>{summary}</span>
          )}
          <FontAwesomeIcon
            icon={isOpen ? faChevronUp : faChevronDown}
            className='text-[10px] text-gray-300 flex-shrink-0'
          />
        </span>
      </button>
      {isOpen && (
        <div className='flex justify-between items-center gap-1 pb-3 px-1'>
          <div className='relative'>
            <input
              type='number' placeholder={t('filtry.od')} value={value.min}
              onChange={e => onChange({ ...value, min: e.target.value })}
              className={inputCls}
            />
            <span className='absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs'>{unit}</span>
          </div>
          <span className='text-gray-400 text-xs'>–</span>
          <div className='relative'>
            <input
              type='number' placeholder={t('filtry.do')} value={value.max}
              onChange={e => onChange({ ...value, max: e.target.value })}
              className={inputCls}
            />
            <span className='absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs'>{unit}</span>
          </div>
        </div>
      )}
    </div>
  )
}

// ----- Collapsible multi-select group (checkbox list, e.g. liczba ogniw) -----
const CollapsibleMultiGroup = ({ groupKey, label, options, selected, onChange, isOpen, onToggle }) => (
  <div className='mb-1 border-b border-gray-50 last:border-0'>
    <button
      type='button'
      onClick={() => onToggle(groupKey)}
      className='w-full flex items-center justify-between gap-2 px-1 py-2.5 rounded-md hover:bg-gray-50 transition-colors text-left'
    >
      <span className='text-[11px] font-semibold text-gray-500 uppercase tracking-wider'>{label}</span>
      <span className='flex items-center gap-2 min-w-0'>
        {selected.length > 0 && (
          <span className='text-[10px] text-blue-600 font-medium'>{selected.join(', ')}</span>
        )}
        <FontAwesomeIcon
          icon={isOpen ? faChevronUp : faChevronDown}
          className='text-[10px] text-gray-300 flex-shrink-0'
        />
      </span>
    </button>
    {isOpen && (
      <div className='pb-3 px-1 flex flex-wrap gap-2'>
        {options.map(opt => {
          const active = selected.includes(opt)
          return (
            <button
              key={opt}
              type='button'
              onClick={() => onChange(active ? selected.filter(v => v !== opt) : [...selected, opt].sort((a, b) => a - b))}
              className={`px-2.5 py-1 rounded-lg text-sm font-medium border transition-colors ${
                active
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'bg-white border-gray-200 text-gray-600 hover:border-blue-300 hover:text-blue-600'
              }`}
            >
              {opt}
            </button>
          )
        })}
      </div>
    )}
  </div>
)

// ----- Section header (collapsible) -----
// `badge` — wybrana wartość pokazana przy nagłówku zwiniętej sekcji. Filtry
// jednokrotnego wyboru (Sprawność, Gwarancja, Cena za Wp) muszą po zwinięciu
// powiedzieć, co jest wybrane; grupy checkboxów pokazują to zaznaczeniami.
// Nagłówek jest JEDEN dla obu rodzajów, żeby lewa kolumna miała jeden rytm.
const SectionHeader = ({ title, isOpen, onToggle, subtle = false, badge = null }) => (
  <div
    className={`flex justify-between items-center gap-2 cursor-pointer rounded-md px-2 py-1.5 transition-colors ${
      subtle ? 'hover:bg-gray-50 text-gray-500' : 'hover:bg-gray-100'
    }`}
    onClick={onToggle}
  >
    {subtle ? (
      <span className='text-xs font-semibold text-gray-400 uppercase tracking-wider'>{title}</span>
    ) : (
      <h4 className='text-sm font-semibold text-blue-600'>{title}</h4>
    )}
    <span className='flex items-center gap-2 min-w-0'>
      {badge && (
        <span className='text-[11px] font-medium text-blue-600 truncate max-w-[7rem]'>{badge}</span>
      )}
      <FontAwesomeIcon
        icon={isOpen ? faChevronUp : faChevronDown}
        className={`flex-shrink-0 text-xs ${subtle ? 'text-gray-300' : 'text-gray-400'}`}
      />
    </span>
  </div>
)

// Odtworzenie stanu UI panelu z zastosowanych filtrów (URL). Odwrotność applyFilters.
const deriveInitialUi = (applied, filtersSchema, initialMaxPrice) => {
  const a = applied && typeof applied === 'object' ? applied : {}
  const tempFilters = {}
  ;(filtersSchema || []).forEach((f) => {
    if (f.type === 'checkbox' && Array.isArray(a[f.name]) && a[f.name].length > 0) {
      tempFilters[f.name] = a[f.name]
    }
  })
  // „Seria" nie jest w filtersSchema (wartości są dynamiczne, zależne od
  // wybranego producenta — patrz renderSeriesFilter), więc odtwarzamy ją
  // ręcznie, tym samym mechanizmem co pozostałe checkboxy.
  if (Array.isArray(a.series) && a.series.length > 0) tempFilters.series = a.series
  const price = Array.isArray(a.price) ? a.price : null
  const minPrice = price ? Number(price[0]) : 0
  const maxPrice = price ? Number(price[1]) : initialMaxPrice
  const power = Array.isArray(a.power) ? a.power : null
  const capacity = Array.isArray(a.capacity) ? a.capacity : null
  const minPower = power && Number(power[0]) > 0 ? String(power[0]) : ''
  const maxPower = power && Number(power[1]) < 1000 ? String(power[1]) : ''
  const minCapacity = capacity && Number(capacity[0]) > 0 ? String(capacity[0]) : ''
  const maxCapacity = capacity && Number(capacity[1]) < 1000 ? String(capacity[1]) : ''
  let selLengthPreset = null
  const lmin = a.module_length_min, lmax = a.module_length_max
  if (Number(lmin) === 2000 && lmax == null) selLengthPreset = 'above_2000'
  else if (Number(lmin) === 1800 && Number(lmax) === 2000) selLengthPreset = '1800_2000'
  else if (Number(lmax) === 1800 && lmin == null) selLengthPreset = 'below_1800'
  const elecRanges = emptyElecRanges()
  ALL_ELEC_RANGES.forEach(r => {
    elecRanges[r.key] = {
      min: a[r.minParam] != null ? String(a[r.minParam]) : '',
      max: a[r.maxParam] != null ? String(a[r.maxParam]) : '',
    }
  })
  return {
    tempFilters,
    minPrice, maxPrice,
    rawMinPrice: String(minPrice), rawMaxPrice: String(maxPrice),
    minPower, maxPower, minCapacity, maxCapacity,
    availableOnly: a.available_only !== undefined ? !!a.available_only : true,
    // Domyślnie POKAZUJEMY oferty „Cena dla zalogowanych" — to legalne oferty B2B,
    // a ukrycie ich bez wiedzy użytkownika zaniżałoby liczbę wyników bez wyjaśnienia.
    publicPriceOnly: a.public_price_only !== undefined ? !!a.public_price_only : false,
    seller: Array.isArray(a.seller) && a.seller.length > 0 ? a.seller : null,
    selEfficiency: a.min_efficiency ?? null,
    selWarranty: a.min_product_warranty ?? null,
    selLengthPreset,
    selWidthRange: a.module_width_range ?? null,
    selThicknessRange: a.module_thickness_range ?? null,
    selMaxWeight: a.max_weight ?? null,
    selFrameColour: a.frame_colour ?? null,
    selMaxPricePerWp: a.max_price_per_wp ?? null,
    selInvWarranty: a.min_warranty ?? null,
    elecRanges,
    selCells: Array.isArray(a.cells_series) ? a.cells_series : [],
    selGamma: a.min_gamma_pmax ?? null,
    selBeta: a.min_beta_voc ?? null,
    selEffMax: a.min_eff_max ?? null,
    selEffEuro: a.min_eff_euro ?? null,
    selCycleLife: a.min_cycle_life ?? null,
    selDod: a.min_dod ?? null,
    selParallel: a.min_parallel_units ?? null,
    selIpRatings: Array.isArray(a.ip_ratings) ? a.ip_ratings : [],
  }
}

// Filtry, których wartości pokazujemy tak jak w danych (marki, liczby, moce, sklepy).
const WARTOSCI_BEZ_TLUMACZENIA = new Set(['manufacturer', 'mppt', 'power_kw', 'seller'])

// ----- Main component -----
const Filters = ({ filtersSchema, onFilterChange, productType, initialFilters = {}, modelMode = false }) => {
  const { t } = useTranslation('lista')
  const initialMaxPrice = productType === 'panel' ? 999 : 49999

  // Odtworzenie UI z URL — liczone raz na mount (panel pozostaje niekontrolowany).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const init = useMemo(() => deriveInitialUi(initialFilters, filtersSchema, initialMaxPrice), [])
  const hasRestoredFilters = Object.keys(initialFilters || {}).filter(k => k !== 'available_only').length > 0

  const getInitialFiltersState = () => {
    const base = {
      price: true,
      ...filtersSchema.reduce((acc, f) => { acc[f.name] = !!init.tempFilters[f.name]; return acc }, {})
    }
    if (productType === 'panel' || productType === 'inwerter') base.power = true
    if (productType === 'magazyn') { base.capacity = true; base.power = false }
    return base
  }

  // ---- basic filter states ----
  const [filters, setFilters] = useState(hasRestoredFilters ? initialFilters : {})
  const [tempFilters, setTempFilters] = useState(init.tempFilters)
  const [minPrice, setMinPrice] = useState(init.minPrice)
  const [maxPrice, setMaxPrice] = useState(init.maxPrice)
  const [rawMinPrice, setRawMinPrice] = useState(init.rawMinPrice)
  const [rawMaxPrice, setRawMaxPrice] = useState(init.rawMaxPrice)
  const [minPower, setMinPower] = useState(init.minPower)
  const [maxPower, setMaxPower] = useState(init.maxPower)
  const [minCapacity, setMinCapacity] = useState(init.minCapacity)
  const [maxCapacity, setMaxCapacity] = useState(init.maxCapacity)
  const [visibleManufacturers, setVisibleManufacturers] = useState(10)
  const [visibleFilters, setVisibleFilters] = useState(getInitialFiltersState)
  const [isFiltering, setIsFiltering] = useState(false)
  const [manufacturerSearch, setManufacturerSearch] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  // Przy którym polu pokazać błąd: 'price' | 'power' | 'capacity', null = pod filtrami.
  // Dawniej rozpoznawane po polskich słowach w treści, co nie działa po tłumaczeniu.
  const [errorField, setErrorField] = useState(null)
  const pokazBlad = (tekst, pole = null) => { setErrorMessage(tekst); setErrorField(pole) }
  const [availableOnly, setAvailableOnly] = useState(init.availableOnly)
  const [publicPriceOnly, setPublicPriceOnly] = useState(init.publicPriceOnly)

  const sellerCatalog = useMemo(() => getSellerCatalog(productType), [productType])
  const allSellerKeys = useMemo(
    () => Object.keys(sellerCatalog).filter(k => k && sellerCatalog[k]),
    [sellerCatalog]
  )

  // ---- seller states ----
  const [selectedSellers, setSelectedSellers] = useState(init.seller || allSellerKeys)
  const allSellersSelected = selectedSellers.length === allSellerKeys.length

  // Reset selekcji sklepów przy zmianie katalogu (kategorii), ale NIE nadpisuj
  // odtworzonej z URL selekcji przy pierwszym renderze.
  const didInitSellers = useRef(false)
  useEffect(() => {
    if (!didInitSellers.current) { didInitSellers.current = true; return }
    setSelectedSellers(allSellerKeys)
  }, [allSellerKeys])

  // ---- advanced section state ----
  const anyAdvancedRestored = !!(
    init.seller || init.selLengthPreset != null || init.selWidthRange != null ||
    init.selThicknessRange != null || init.selMaxWeight != null || init.selFrameColour != null ||
    init.selInvWarranty != null ||
    init.selCells.length > 0 || init.selGamma != null || init.selBeta != null ||
    init.selEffMax != null || init.selEffEuro != null ||
    init.selDod != null || init.selParallel != null || init.selIpRatings.length > 0 ||
    ALL_ELEC_RANGES.some(r => init.elecRanges[r.key]?.min !== '' || init.elecRanges[r.key]?.max !== '')
  )
  const [showAdvanced, setShowAdvanced] = useState(anyAdvancedRestored)
  const [openAdvancedGroup, setOpenAdvancedGroup] = useState(null)

  const toggleAdvancedGroup = (key) => {
    setOpenAdvancedGroup(prev => (prev === key ? null : key))
  }

  // Advanced preset states — null means "no selection"
  const [selEfficiency, setSelEfficiency] = useState(init.selEfficiency)
  const [selWarranty, setSelWarranty] = useState(init.selWarranty)
  const [selLengthPreset, setSelLengthPreset] = useState(init.selLengthPreset)
  const [selWidthRange, setSelWidthRange] = useState(init.selWidthRange)
  const [selThicknessRange, setSelThicknessRange] = useState(init.selThicknessRange)
  const [selMaxWeight, setSelMaxWeight] = useState(init.selMaxWeight)
  const [selFrameColour, setSelFrameColour] = useState(init.selFrameColour)
  const [selMaxPricePerWp, setSelMaxPricePerWp] = useState(init.selMaxPricePerWp)
  const [selInvWarranty, setSelInvWarranty] = useState(init.selInvWarranty)

  // Parametry elektryczne (dane z kart katalogowych)
  const [elecRanges, setElecRanges] = useState(init.elecRanges)
  const [selCells, setSelCells] = useState(init.selCells)
  const [selGamma, setSelGamma] = useState(init.selGamma)
  const [selBeta, setSelBeta] = useState(init.selBeta)
  const [selEffMax, setSelEffMax] = useState(init.selEffMax)
  const [selEffEuro, setSelEffEuro] = useState(init.selEffEuro)
  const [selCycleLife, setSelCycleLife] = useState(init.selCycleLife)
  const [selDod, setSelDod] = useState(init.selDod)
  const [selParallel, setSelParallel] = useState(init.selParallel)
  const [selIpRatings, setSelIpRatings] = useState(init.selIpRatings)

  const setElecRange = (key, value) => setElecRanges(prev => ({ ...prev, [key]: value }))

  // Licznik aktualnie ustawionych (oczekujących) filtrów — pokazywany w przycisku.
  const activeFilterCount = useMemo(() => {
    let c = 0
    Object.values(tempFilters).forEach(v => { if (Array.isArray(v)) c += v.length })
    if (String(minPower) !== '' || String(maxPower) !== '') c += 1
    if (String(minCapacity) !== '' || String(maxCapacity) !== '') c += 1
    if (Number(rawMinPrice) > 0 || Number(rawMaxPrice) < initialMaxPrice) c += 1
    if (!allSellersSelected) c += 1
    ;[selEfficiency, selWarranty, selLengthPreset, selWidthRange,
      selThicknessRange, selMaxWeight, selFrameColour, selMaxPricePerWp, selInvWarranty,
      selGamma, selBeta, selEffMax, selEffEuro, selCycleLife, selDod, selParallel]
      .forEach(s => { if (s !== null && s !== undefined) c += 1 })
    if (selCells.length > 0) c += 1
    if (selIpRatings.length > 0) c += 1
    ALL_ELEC_RANGES.forEach(r => {
      if (elecRanges[r.key]?.min !== '' || elecRanges[r.key]?.max !== '') c += 1
    })
    return c
  }, [
    tempFilters, minPower, maxPower, minCapacity, maxCapacity,
    rawMinPrice, rawMaxPrice, allSellersSelected, initialMaxPrice,
    selEfficiency, selWarranty, selLengthPreset, selWidthRange,
    selThicknessRange, selMaxWeight, selFrameColour, selMaxPricePerWp, selInvWarranty,
    selGamma, selBeta, selEffMax, selEffEuro, selCells, elecRanges,
    selCycleLife, selDod, selParallel, selIpRatings,
  ])

  // ---- manufacturer search (local, no server call) ----
  const manufacturerFilterSchema = useMemo(
    () => filtersSchema?.find(f => f.type === 'checkbox' && f.name === 'manufacturer'),
    [filtersSchema]
  )

  const filteredManufacturerEntries = useMemo(() => {
    if (!manufacturerFilterSchema) return []
    const entries = Object.entries(manufacturerFilterSchema.values)
    const q = manufacturerSearch.toLowerCase()
    const filtered = q ? entries.filter(([key]) => key.toLowerCase().includes(q)) : entries
    return filtered.sort(([a], [b]) => a.localeCompare(b, 'pl'))
  }, [manufacturerFilterSchema, manufacturerSearch])

  // ---- helpers ----
  const toggleFilter = (name) =>
    setVisibleFilters(prev => ({ ...prev, [name]: !prev[name] }))

  const handleTempFilterChange = (name, value) =>
    setTempFilters(prev => ({ ...prev, [name]: value }))

  // ---- filtr „Seria" — dochodzi TYLKO gdy w „Producent" wybrano dokładnie
  // jednego. Ta sama nazwa serii bywa inna u dwóch producentów, więc przy
  // wielu wybranych filtr nie miałby jednoznacznego znaczenia (patrz
  // series.py w backendzie: reguły działają w obrębie jednego producenta).
  // Dostępne wyłącznie w widoku „Grupuj oferty" — seria jest cechą MODELU,
  // a widok ofert (Panels/Inverters/Magazines) jej nie zna.
  const wybranyProducent = tempFilters.manufacturer?.length === 1 ? tempFilters.manufacturer[0] : null
  const [seriesOptions, setSeriesOptions] = useState([])
  const [seriesLoading, setSeriesLoading] = useState(false)

  useEffect(() => {
    if (!modelMode || !wybranyProducent) {
      setSeriesOptions([])
      return undefined
    }
    let anulowane = false
    setSeriesLoading(true)
    fetchSeriesOptions(productType, wybranyProducent).then((opcje) => {
      if (anulowane) return
      setSeriesOptions(opcje)
      setSeriesLoading(false)
    })
    return () => { anulowane = true }
  }, [modelMode, productType, wybranyProducent])

  // Producent przestał być pojedynczy (odznaczony/dodano drugiego) albo zmienił
  // się widok — poprzedni wybór serii traci sens i ma zniknąć, nie zostać
  // „widmowym" filtrem, który nic już nie zawęża.
  const poprzedniProducent = useRef(wybranyProducent)
  useEffect(() => {
    if (poprzedniProducent.current !== wybranyProducent) {
      poprzedniProducent.current = wybranyProducent
      if (tempFilters.series?.length > 0) handleTempFilterChange('series', [])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wybranyProducent, modelMode])

  const handleToggleAllSellers = (checked) =>
    setSelectedSellers(checked ? allSellerKeys : [])

  const handleToggleSeller = (key, checked) =>
    setSelectedSellers(prev =>
      checked ? (prev.includes(key) ? prev : [...prev, key]) : prev.filter(s => s !== key)
    )

  // ---- apply ----
  const applyFilters = () => {
    if (isFiltering) return

    if (Number(rawMaxPrice) < Number(rawMinPrice)) {
      pokazBlad(t('filtry.blad_cena'), 'price')
      return
    }
    if (minPower !== '' && maxPower !== '' && Number(maxPower) < Number(minPower)) {
      pokazBlad(t('filtry.blad_moc'), 'power')
      return
    }
    if (minCapacity !== '' && maxCapacity !== '' && Number(maxCapacity) < Number(minCapacity)) {
      pokazBlad(t('filtry.blad_pojemnosc'), 'capacity')
      return
    }

    const aktywneRanges = productType === 'panel' ? ELEC_RANGES_PANEL
      : productType === 'inwerter' ? ELEC_RANGES_INVERTER
      : productType === 'magazyn' ? ELEC_RANGES_MAGAZINE : []
    for (const r of aktywneRanges) {
      const v = elecRanges[r.key]
      if (v.min !== '' && v.max !== '' && Number(v.max) < Number(v.min)) {
        pokazBlad(t('filtry.blad_zakres', { nazwa: t(`filtry.elektryczne.${r.key}`) }))
        return
      }
    }

    setIsFiltering(true)

    const minPriceValue = rawMinPrice === '' ? 0 : Number(rawMinPrice)
    const maxPriceValue = rawMaxPrice === '' ? initialMaxPrice : Number(rawMaxPrice)
    setMinPrice(minPriceValue)
    setMaxPrice(maxPriceValue)

    // Wysyłaj price/power/capacity do API tylko gdy user faktycznie zawęził zakres —
    // nietknięty suwak zostaje domyślny wizualnie, ale nie trafia do query params.
    const powerArr = [minPower === '' ? 0 : minPower, maxPower === '' ? 1000 : maxPower]
    const capacityArr = [minCapacity === '' ? 0 : minCapacity, maxCapacity === '' ? 1000 : maxCapacity]

    const updatedFilters = {
      ...tempFilters,
      available_only: availableOnly,
    }
    // Tylko gdy zaznaczone: `false` w obiekcie liczyłoby się jako „użyty filtr"
    // i URL przestawałby być czysty w stanie domyślnym (patrz encodeFilters).
    if (publicPriceOnly) updatedFilters.public_price_only = true
    if (minPriceValue !== 0 || maxPriceValue !== initialMaxPrice) {
      updatedFilters.price = [minPriceValue, maxPriceValue]
    }
    if (Number(powerArr[0]) !== 0 || Number(powerArr[1]) !== 1000) {
      updatedFilters.power = powerArr
    }
    if (Number(capacityArr[0]) !== 0 || Number(capacityArr[1]) !== 1000) {
      updatedFilters.capacity = capacityArr
    }

    if (productType === 'panel') {
      // Seller filter
      if (selectedSellers.length > 0 && selectedSellers.length < allSellerKeys.length) {
        updatedFilters.seller = selectedSellers
      }

      // Advanced presets
      if (selEfficiency !== null) updatedFilters.min_efficiency = selEfficiency
      if (selWarranty !== null) updatedFilters.min_product_warranty = selWarranty

      if (selLengthPreset === 'above_2000') {
        updatedFilters.module_length_min = 2000
      } else if (selLengthPreset === '1800_2000') {
        updatedFilters.module_length_min = 1800
        updatedFilters.module_length_max = 2000
      } else if (selLengthPreset === 'below_1800') {
        updatedFilters.module_length_max = 1800
      }

      if (selWidthRange) updatedFilters.module_width_range = selWidthRange
      if (selThicknessRange) updatedFilters.module_thickness_range = selThicknessRange
      if (selMaxWeight !== null) updatedFilters.max_weight = selMaxWeight
      if (selFrameColour) updatedFilters.frame_colour = selFrameColour
      if (selMaxPricePerWp !== null) updatedFilters.max_price_per_wp = selMaxPricePerWp

      // Parametry elektryczne (dane z kart katalogowych)
      ELEC_RANGES_PANEL.forEach(r => {
        const v = elecRanges[r.key]
        if (v.min !== '') updatedFilters[r.minParam] = Number(v.min)
        if (v.max !== '') updatedFilters[r.maxParam] = Number(v.max)
      })
      if (selCells.length > 0) updatedFilters.cells_series = selCells
      if (selGamma !== null) updatedFilters.min_gamma_pmax = selGamma
      if (selBeta !== null) updatedFilters.min_beta_voc = selBeta
    }

    if (productType === 'inwerter') {
      if (selectedSellers.length > 0 && selectedSellers.length < allSellerKeys.length) {
        updatedFilters.seller = selectedSellers
      }
      if (selInvWarranty !== null) updatedFilters.min_warranty = selInvWarranty

      // Parametry elektryczne (dane z kart katalogowych)
      ELEC_RANGES_INVERTER.forEach(r => {
        const v = elecRanges[r.key]
        if (v.min !== '') updatedFilters[r.minParam] = Number(v.min)
        if (v.max !== '') updatedFilters[r.maxParam] = Number(v.max)
      })
      if (selEffMax !== null) updatedFilters.min_eff_max = selEffMax
      if (selEffEuro !== null) updatedFilters.min_eff_euro = selEffEuro
    }

    if (productType === 'magazyn') {
      if (selectedSellers.length > 0 && selectedSellers.length < allSellerKeys.length) {
        updatedFilters.seller = selectedSellers
      }
      if (selCycleLife !== null) updatedFilters.min_cycle_life = selCycleLife
      if (selDod !== null) updatedFilters.min_dod = selDod
      if (selParallel !== null) updatedFilters.min_parallel_units = selParallel
      if (selIpRatings.length > 0) updatedFilters.ip_ratings = selIpRatings

      // Parametry elektryczne (dane z kart katalogowych)
      ELEC_RANGES_MAGAZINE.forEach(r => {
        const v = elecRanges[r.key]
        if (v.min !== '') updatedFilters[r.minParam] = Number(v.min)
        if (v.max !== '') updatedFilters[r.maxParam] = Number(v.max)
      })
    }

    setFilters(updatedFilters)
    onFilterChange(updatedFilters)
    pokazBlad('')
    setTimeout(() => setIsFiltering(false), 1000)
  }

  // ---- clear ----
  const clearFilters = () => {
    setFilters({})
    setMinPrice(0)
    setMaxPrice(initialMaxPrice)
    setRawMinPrice('0')
    setRawMaxPrice(initialMaxPrice.toString())
    setMinPower('')
    setMaxPower('')
    setMinCapacity('')
    setMaxCapacity('')
    setSelectedSellers(allSellerKeys)
    setShowAdvanced(false)
    setOpenAdvancedGroup(null)
    setSelEfficiency(null)
    setSelWarranty(null)
    setSelLengthPreset(null)
    setSelWidthRange(null)
    setSelThicknessRange(null)
    setSelMaxWeight(null)
    setSelFrameColour(null)
    setSelMaxPricePerWp(null)
    setSelInvWarranty(null)
    setElecRanges(emptyElecRanges())
    setSelCells([])
    setSelGamma(null)
    setSelBeta(null)
    setSelEffMax(null)
    setSelEffEuro(null)
    setSelCycleLife(null)
    setSelDod(null)
    setSelParallel(null)
    setSelIpRatings([])
    setManufacturerSearch('')
    setTempFilters({})
    setAvailableOnly(true)
    setPublicPriceOnly(false)
    onFilterChange({ available_only: true })
    pokazBlad('')
  }

  // ====== RENDER SECTIONS ======

  const renderPriceFilter = () => (
    <div className='border-b border-gray-100 pb-4 mb-4'>
      <SectionHeader
        title={t('filtry.cena')}
        isOpen={visibleFilters.price}
        onToggle={() => toggleFilter('price')}
      />
      {visibleFilters.price && (
        <div className='mt-3 px-1'>
          <MultiRangeSlider
            minPrice={minPrice}
            maxPrice={maxPrice}
            setMinPrice={setMinPrice}
            setMaxPrice={setMaxPrice}
            rawMinPrice={rawMinPrice}
            rawMaxPrice={rawMaxPrice}
            setRawMinPrice={setRawMinPrice}
            setRawMaxPrice={setRawMaxPrice}
            productType={productType}
          />
          {errorMessage && errorField === 'price' && (
            <p className='text-red-500 text-xs mt-2 text-center'>{errorMessage}</p>
          )}
        </div>
      )}
    </div>
  )

  const renderPowerFilter = () => (
    <div className='border-b border-gray-100 pb-4 mb-4'>
      <SectionHeader
        title={t('filtry.moc')}
        isOpen={visibleFilters.power}
        onToggle={() => toggleFilter('power')}
      />
      {visibleFilters.power && (
        <div className='mt-3 px-1'>
          <div className='flex justify-between items-center'>
            <div className='relative'>
              <input
                type='number' min='0' max='1000' placeholder={t('filtry.od')}
                value={minPower}
                onChange={e => setMinPower(e.target.value)}
                className='px-3 py-2 border border-gray-200 rounded-lg w-24 text-center pr-8 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400'
              />
              <span className='absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs'>
                {productType === 'panel' ? 'Wp' : 'kW'}
              </span>
            </div>
            <span className='text-gray-400 text-xs'>–</span>
            <div className='relative'>
              <input
                type='number' min='0' max='1000' placeholder={t('filtry.do')}
                value={maxPower}
                onChange={e => setMaxPower(e.target.value)}
                className='px-3 py-2 border border-gray-200 rounded-lg w-24 text-center pr-8 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400'
              />
              <span className='absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs'>
                {productType === 'panel' ? 'Wp' : 'kW'}
              </span>
            </div>
          </div>
          {errorMessage && errorField === 'power' && (
            <p className='text-red-500 text-xs mt-2 text-center'>{errorMessage}</p>
          )}
        </div>
      )}
    </div>
  )

  const renderCapacityFilter = () => (
    <div className='border-b border-gray-100 pb-4 mb-4'>
      <SectionHeader
        title={t('filtry.pojemnosc')}
        isOpen={visibleFilters.capacity}
        onToggle={() => toggleFilter('capacity')}
      />
      {visibleFilters.capacity && (
        <div className='mt-3 px-1'>
          <div className='flex justify-between items-center'>
            <div className='relative'>
              <input
                type='number' min='0' placeholder={t('filtry.od')}
                value={minCapacity}
                onChange={e => setMinCapacity(e.target.value)}
                className='px-3 py-2 border border-gray-200 rounded-lg w-24 text-center pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400'
              />
              <span className='absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs'>kWh</span>
            </div>
            <span className='text-gray-400 text-xs'>–</span>
            <div className='relative'>
              <input
                type='number' min='0' placeholder={t('filtry.do')}
                value={maxCapacity}
                onChange={e => setMaxCapacity(e.target.value)}
                className='px-3 py-2 border border-gray-200 rounded-lg w-24 text-center pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400'
              />
              <span className='absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs'>kWh</span>
            </div>
          </div>
          {errorMessage && errorField === 'capacity' && (
            <p className='text-red-500 text-xs mt-2 text-center'>{errorMessage}</p>
          )}
        </div>
      )}
    </div>
  )

  const renderAvailabilityFilter = () => (
    <div className='border-b border-gray-100 pb-4 mb-4'>
      <label className='flex items-center gap-2 cursor-pointer group px-1 py-1.5'>
        <StyledCheckbox checked={availableOnly} onChange={(e) => setAvailableOnly(e.target.checked)} />
        <span className='text-sm font-medium text-gray-700 group-hover:text-gray-900'>{t('filtry.tylko_dostepne')}</span>
      </label>
      {/* Chowa oferty B2B z ceną widoczną dopiero po zalogowaniu w sklepie —
          przy porównywaniu cen taka pozycja bywa tylko szumem. */}
      <label className='flex items-center gap-2 cursor-pointer group px-1 py-1.5'>
        <StyledCheckbox checked={publicPriceOnly} onChange={(e) => setPublicPriceOnly(e.target.checked)} />
        <span className='text-sm font-medium text-gray-700 group-hover:text-gray-900'>{t('filtry.tylko_jawne_ceny')}</span>
      </label>
    </div>
  )

  // Etykiety ze schematu (filtersSchema.js) tłumaczone przy renderze. Klucze i wartości
  // wysyłane do API zostają bez zmian (to wartości z bazy, np. „Hybrydowy", „1 Fazowy").
  const tytulFiltra = (f) => t(`filtry.schemat.tytul.${f.name}`, { defaultValue: f.title })
  const podpowiedzFiltra = (f) => (f.hint ? t(`filtry.schemat.podpowiedz.${f.name}`, { defaultValue: f.hint }) : null)
  const etykietaWartosci = (f, key, label) => (
    WARTOSCI_BEZ_TLUMACZENIA.has(f.name)
      ? label
      : t(`filtry.schemat.wartosci.${f.name}.${key}`, { defaultValue: label })
  )

  // Rodzaj falownika jako gotowe przyciski (nie checkboxy) — pozycjonowany osobno
  // między Ceną a Mocą (na życzenie, tylko dla /inwertery), więc renderCheckboxFilters
  // poniżej pomija ten filtr dla productType === 'inwerter'.
  const renderTypeButtonsFilter = () => {
    const filter = filtersSchema.find(f => f.type === 'checkbox' && f.name === 'types')
    if (!filter) return null
    const entries = Object.entries(filter.values)

    return (
      <div className='border-b border-gray-100 pb-4 mb-4'>
        <SectionHeader
          title={tytulFiltra(filter)}
          isOpen={visibleFilters[filter.name]}
          onToggle={() => toggleFilter(filter.name)}
        />
        {visibleFilters[filter.name] && (
          <div className='mt-3 px-1 flex flex-wrap gap-2'>
            {entries.map(([key, label]) => {
              const active = tempFilters[filter.name]?.includes(key) || false
              return (
                <button
                  key={key}
                  type='button'
                  onClick={() => {
                    const prev = tempFilters[filter.name] || []
                    const next = active ? prev.filter(v => v !== key) : [...prev, key]
                    handleTempFilterChange(filter.name, next)
                  }}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                    active
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : 'bg-white border-gray-200 text-gray-600 hover:border-blue-300 hover:text-blue-600'
                  }`}
                >
                  {etykietaWartosci(filter, key, label)}
                </button>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  const renderCheckboxFilters = () =>
    filtersSchema.map((filter) => {
      if (filter.type !== 'checkbox') return null
      // Rodzaj falownika renderowany osobno (renderTypeButtonsFilter), między Ceną a Mocą.
      if (productType === 'inwerter' && filter.name === 'types') return null
      // Temperatura ładowania renderowana osobno, POD liczbą cykli
      // (renderMagazineMainPresets) — kolejność filtrów głównych jest celowa.
      if (filter.name === 'charge_temp_class') return null
      return (
        <React.Fragment key={filter.name}>
          {renderCheckboxFilter(filter)}
          {/* Zaraz POD „Producent" — Seria doprecyzowuje wybór producenta,
              więc ma stać jak najbliżej niego, nie na końcu listy filtrów. */}
          {filter.name === 'manufacturer' && renderSeriesFilter()}
        </React.Fragment>
      )
    })

  const renderCheckboxFilter = (filter) => {
      const isManufacturer = filter.name === 'manufacturer'
      const allEntries = isManufacturer
        ? filteredManufacturerEntries
        : Object.entries(filter.values)
      const visibleEntries = allEntries.slice(0, visibleManufacturers)
      const hasMore = allEntries.length > visibleManufacturers

      return (
        <div key={filter.name} className='border-b border-gray-100 pb-4 mb-4'>
          <SectionHeader
            title={tytulFiltra(filter)}
            isOpen={visibleFilters[filter.name]}
            onToggle={() => toggleFilter(filter.name)}
          />
          {visibleFilters[filter.name] && (
            <div className='mt-3 px-1'>
              {filter.hint && (
                <p className='text-[11px] text-gray-400 mb-2 leading-snug'>{podpowiedzFiltra(filter)}</p>
              )}
              {isManufacturer && (
                <input
                  type='text'
                  value={manufacturerSearch}
                  onChange={e => setManufacturerSearch(e.target.value)}
                  placeholder={t('filtry.szukaj_producenta')}
                  onClick={e => e.stopPropagation()}
                  className='w-full px-3 py-1.5 mb-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-400 bg-gray-50'
                />
              )}
              <div className='space-y-2'>
                {visibleEntries.map(([key, label]) => (
                  <label key={key} className='flex items-center gap-2 cursor-pointer group'>
                    <StyledCheckbox
                      name={filter.name}
                      value={key}
                      checked={tempFilters[filter.name]?.includes(key) || false}
                      onChange={e => {
                        const prev = tempFilters[filter.name] || []
                        const next = e.target.checked
                          ? [...prev, key]
                          : prev.filter(v => v !== key)
                        handleTempFilterChange(filter.name, next)
                      }}
                    />
                    <span className='text-sm text-gray-600 group-hover:text-gray-800'>{etykietaWartosci(filter, key, label)}</span>
                  </label>
                ))}
              </div>
              {hasMore && (
                <button
                  onClick={() => setVisibleManufacturers(p => p + 10)}
                  className='mt-3 text-xs text-blue-500 hover:text-blue-700 hover:underline block'
                >
                  {t('filtry.wyswietl_wiecej')}
                </button>
              )}
            </div>
          )}
        </div>
      )
  }

  // Filtr „Seria" — dynamiczny, nie ma go w filtersSchema (wartości zależą od
  // wybranego producenta, patrz useEffect wyżej). Renderowany jako osobna
  // sekcja z tym samym rytmem wizualnym co pozostałe checkboxy.
  const renderSeriesFilter = () => {
    if (!modelMode || !wybranyProducent) return null
    return (
      <div className='border-b border-gray-100 pb-4 mb-4'>
        <SectionHeader
          title={t('filtry.seria')}
          isOpen={visibleFilters.series ?? true}
          onToggle={() => toggleFilter('series')}
          badge={wybranyProducent}
        />
        {(visibleFilters.series ?? true) && (
          <div className='mt-3 px-1'>
            {seriesLoading ? (
              <p className='text-xs text-gray-400'>{t('filtry.wczytywanie')}</p>
            ) : seriesOptions.length === 0 ? (
              <p className='text-xs text-gray-400 leading-snug'>
                {t('filtry.brak_serii', { producent: wybranyProducent })}
              </p>
            ) : (
              <div className='space-y-2'>
                {seriesOptions.map(({ value, count }) => (
                  <label key={value} className='flex items-center gap-2 cursor-pointer group'>
                    <StyledCheckbox
                      name='series'
                      value={value}
                      checked={tempFilters.series?.includes(value) || false}
                      onChange={(e) => {
                        const prev = tempFilters.series || []
                        const next = e.target.checked
                          ? [...prev, value]
                          : prev.filter((v) => v !== value)
                        handleTempFilterChange('series', next)
                      }}
                    />
                    <span className='text-sm text-gray-600 group-hover:text-gray-800'>
                      {value} <span className='text-gray-400'>({count})</span>
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  // Liczba cykli i temperatura ładowania stoją w sekcji GŁÓWNEJ, zaraz pod
  // Napięciem: to dwa parametry, które realnie rozstrzygają wybór magazynu
  // (ile lat pożyje i czy ruszy zimą), a nie ciekawostki dla zaawansowanych.
  const renderMagazineMainPresets = () => {
    const tempSchema = filtersSchema.find(f => f.name === 'charge_temp_class')
    return (
      <>
        <CollapsiblePresetGroup
          groupKey='cycleLife'
          label={t('filtry.liczba_cykli')}
          wariant='glowny'
          selected={selCycleLife}
          onSelect={setSelCycleLife}
          isOpen={openAdvancedGroup === 'cycleLife'}
          onToggle={toggleAdvancedGroup}
          options={ADVANCED_MAG_CYCLE_OPTS(t)}
        />
        {tempSchema && renderCheckboxFilter(tempSchema)}
      </>
    )
  }

  const renderSellerFilter = () => {
    const sellerSummary = allSellersSelected
      ? null
      : t('filtry.wybrano', { liczba: selectedSellers.length })

    return (
      <div className='mb-1 border-b border-gray-50'>
        <button
          type='button'
          onClick={() => toggleAdvancedGroup('seller')}
          className='w-full flex items-center justify-between gap-2 px-1 py-2.5 rounded-md hover:bg-gray-50 transition-colors text-left'
        >
          <span className='text-[11px] font-semibold text-gray-500 uppercase tracking-wider'>
            {t('filtry.sklep')}
          </span>
          <span className='flex items-center gap-2 min-w-0'>
            {sellerSummary && (
              <span className='text-[10px] text-blue-600 font-medium'>{sellerSummary}</span>
            )}
            <FontAwesomeIcon
              icon={openAdvancedGroup === 'seller' ? faChevronUp : faChevronDown}
              className='text-[10px] text-gray-300 flex-shrink-0'
            />
          </span>
        </button>
        {openAdvancedGroup === 'seller' && (
          <div className='mt-1 px-1 pb-3 space-y-2'>
            <label className='flex items-center gap-2 cursor-pointer group'>
              <StyledCheckbox
                checked={allSellersSelected}
                onChange={e => handleToggleAllSellers(e.target.checked)}
              />
              <span className='text-sm font-medium text-gray-700'>{t('filtry.wszystkie')}</span>
            </label>
            {Object.entries(sellerCatalog)
              .filter(([key, label]) => key && label)
              .map(([key, label]) => (
                <label key={key} className='flex items-center gap-2 cursor-pointer group'>
                  <StyledCheckbox
                    value={key}
                    checked={selectedSellers.includes(key)}
                    onChange={e => handleToggleSeller(key, e.target.checked)}
                  />
                  <span className='text-sm text-gray-600 group-hover:text-gray-800'>{label}</span>
                </label>
              ))}
          </div>
        )}
      </div>
    )
  }


  const ADVANCED_EFFICIENCY_OPTS =
    opcje([24, 23, 22, 21, 20], (v) => t('filtry.opcje.powyzej_procent', { wartosc: procent(v) }))
  const ADVANCED_WARRANTY_OPTS = opcje([25, 20, 15], (v) => t('filtry.opcje.od_lat', { count: v }))
  const ADVANCED_LENGTH_OPTS = [
    { label: t('filtry.opcje.od_mm', { wartosc: 2000 }), value: 'above_2000' },
    { label: t('filtry.opcje.od_do_mm', { min: 1800, max: 2000 }), value: '1800_2000' },
    { label: t('filtry.opcje.do_mm', { wartosc: 1800 }), value: 'below_1800' },
  ]
  const ADVANCED_WIDTH_OPTS = [
    { label: t('filtry.opcje.od_mm', { wartosc: 1134 }), value: 'above_1134' },
    { label: t('filtry.opcje.ponizej_mm', { wartosc: 1134 }), value: 'below_1134' },
  ]
  const ADVANCED_THICKNESS_OPTS = [
    { label: t('filtry.opcje.mm', { wartosc: 30 }), value: '30' },
    { label: t('filtry.opcje.powyzej_mm', { wartosc: 30 }), value: 'above_30' },
  ]
  const ADVANCED_WEIGHT_OPTS = opcje([20, 22, 25, 30], (v) => t('filtry.opcje.ponizej_kg', { wartosc: v }))
  // Wartość to tekst z bazy (frame_colour), więc zostaje po polsku; tłumaczymy etykietę.
  const ADVANCED_FRAME_OPTS = [
    { label: t('filtry.opcje.czarna_rama'), value: 'Czarna rama' },
    { label: t('filtry.opcje.srebrna_rama'), value: 'Srebrna rama' },
  ]
  // Waluta kraju w etykiecie; progi liczbowe bez zmian.
  const ADVANCED_PRICE_WP_OPTS = opcje([0.50, 0.55, 0.60, 0.65], (v) =>
    t('filtry.opcje.ponizej_cena_wp', { cena: formatPrice(v, 2), waluta: KRAJ.symbol }))
  const ADVANCED_INV_WARRANTY_OPTS = opcje([12, 10, 5], (v) => t('filtry.opcje.od_lat', { count: v }))

  // Nagłówek podsekcji „Parametry elektryczne" z dopiskiem o pochodzeniu danych.
  // Dopisek jest ważny: filtr elektryczny pokazuje TYLKO modele o znanych
  // parametrach (z kart producenta) — model bez karty odpada, i to ma być jawne.
  const renderElectricalHeader = () => (
    <>
      <p className='text-xs font-bold text-gray-700 mb-1 mt-4 px-1'>{t('filtry.parametry_elektryczne')}</p>
      <p className='text-[10px] text-gray-400 px-1 mb-2 leading-snug'>
        {t('filtry.parametry_elektryczne_opis')}
      </p>
    </>
  )

  const renderElectricalRanges = (ranges) =>
    ranges.map(r => (
      <CollapsibleRangeGroup
        key={r.key}
        groupKey={r.key}
        label={t(`filtry.elektryczne.${r.key}`)}
        unit={r.unit}
        value={elecRanges[r.key]}
        onChange={v => setElecRange(r.key, v)}
        isOpen={openAdvancedGroup === r.key}
        onToggle={toggleAdvancedGroup}
      />
    ))

  const renderAdvancedFilters = () => {
    if (productType === 'inwerter') {
      const hasPartialSellerFilter =
        selectedSellers.length > 0 && selectedSellers.length < allSellerKeys.length
      const hasAnyInvAdvanced = hasPartialSellerFilter || selInvWarranty !== null ||
        selEffMax !== null || selEffEuro !== null ||
        ELEC_RANGES_INVERTER.some(r => elecRanges[r.key]?.min !== '' || elecRanges[r.key]?.max !== '')

      return (
        <div className='mt-2'>
          <div
            className='flex justify-between items-center cursor-pointer rounded-md px-2 py-2 hover:bg-gray-50 transition-colors border-t border-gray-100 pt-4'
            onClick={() => setShowAdvanced(p => !p)}
          >
            <span className='text-xs font-bold text-gray-500 uppercase tracking-wider'>
              {t('filtry.zaawansowane')}
              {hasAnyInvAdvanced && (
                <span className='ml-2 inline-flex items-center justify-center w-4 h-4 bg-blue-600 text-white rounded-full text-[9px]'>
                  ✓
                </span>
              )}
            </span>
            <FontAwesomeIcon
              icon={showAdvanced ? faChevronUp : faChevronDown}
              className='text-gray-300 text-xs'
            />
          </div>

          {showAdvanced && (
            <div className='mt-2 pt-1 pb-2'>
              <p className='text-xs font-bold text-gray-700 mb-2 px-1'>{t('filtry.sekcja_falowniki')}</p>
              {renderSellerFilter()}
              <CollapsiblePresetGroup
                groupKey='invWarranty'
                label={t('filtry.gwarancja')}
                selected={selInvWarranty}
                onSelect={setSelInvWarranty}
                isOpen={openAdvancedGroup === 'invWarranty'}
                onToggle={toggleAdvancedGroup}
                options={ADVANCED_INV_WARRANTY_OPTS}
              />

              {renderElectricalHeader()}
              {renderElectricalRanges(ELEC_RANGES_INVERTER)}
              <CollapsiblePresetGroup
                groupKey='effMax'
                label={t('filtry.sprawnosc_maksymalna')}
                selected={selEffMax}
                onSelect={setSelEffMax}
                isOpen={openAdvancedGroup === 'effMax'}
                onToggle={toggleAdvancedGroup}
                options={ADVANCED_INV_EFF_MAX_OPTS(t)}
              />
              <CollapsiblePresetGroup
                groupKey='effEuro'
                label={t('filtry.sprawnosc_europejska')}
                selected={selEffEuro}
                onSelect={setSelEffEuro}
                isOpen={openAdvancedGroup === 'effEuro'}
                onToggle={toggleAdvancedGroup}
                options={ADVANCED_INV_EFF_EURO_OPTS(t)}
              />
            </div>
          )}
        </div>
      )
    }

    if (productType === 'magazyn') {
      const hasPartialSellerFilter =
        selectedSellers.length > 0 && selectedSellers.length < allSellerKeys.length
      const hasAnyMagAdvanced =
        hasPartialSellerFilter || selDod !== null || selParallel !== null ||
        selIpRatings.length > 0 ||
        ELEC_RANGES_MAGAZINE.some(r => elecRanges[r.key]?.min !== '' || elecRanges[r.key]?.max !== '')

      return (
        <div className='mt-2'>
          <div
            className='flex justify-between items-center cursor-pointer rounded-md px-2 py-2 hover:bg-gray-50 transition-colors border-t border-gray-100 pt-4'
            onClick={() => setShowAdvanced(p => !p)}
          >
            <span className='text-xs font-bold text-gray-500 uppercase tracking-wider'>
              {t('filtry.zaawansowane')}
              {hasAnyMagAdvanced && (
                <span className='ml-2 inline-flex items-center justify-center w-4 h-4 bg-blue-600 text-white rounded-full text-[9px]'>
                  ✓
                </span>
              )}
            </span>
            <FontAwesomeIcon
              icon={showAdvanced ? faChevronUp : faChevronDown}
              className='text-gray-300 text-xs'
            />
          </div>

          {showAdvanced && (
            <div className='mt-2 pt-1 pb-2'>
              <p className='text-xs font-bold text-gray-700 mb-2 px-1'>{t('filtry.sekcja_magazyny')}</p>
              {renderSellerFilter()}

              {renderElectricalHeader()}
              {renderElectricalRanges(ELEC_RANGES_MAGAZINE)}

              <CollapsiblePresetGroup
                groupKey='magDod'
                label={t('filtry.glebokosc_rozladowania')}
                selected={selDod}
                onSelect={setSelDod}
                isOpen={openAdvancedGroup === 'magDod'}
                onToggle={toggleAdvancedGroup}
                options={ADVANCED_MAG_DOD_OPTS(t)}
              />
              <CollapsiblePresetGroup
                groupKey='magParallel'
                label={t('filtry.modulow_rownolegle')}
                selected={selParallel}
                onSelect={setSelParallel}
                isOpen={openAdvancedGroup === 'magParallel'}
                onToggle={toggleAdvancedGroup}
                options={ADVANCED_MAG_PARALLEL_OPTS(t)}
              />

              {/* Klasa szczelności — wielokrotny wybór, bo IP20 i IP21 to jedna
                  decyzja („tylko do wnętrz"), a IP54+ druga. */}
              <div className='mb-1 border-b border-gray-50'>
                <button
                  type='button'
                  onClick={() => toggleAdvancedGroup('magIp')}
                  className='w-full flex items-center justify-between gap-2 px-1 py-2.5 rounded-md hover:bg-gray-50 transition-colors text-left'
                >
                  <span className='text-[11px] font-semibold text-gray-500 uppercase tracking-wider'>
                    {t('filtry.stopien_ochrony')}
                  </span>
                  <span className='flex items-center gap-2 min-w-0'>
                    {selIpRatings.length > 0 && (
                      <span className='text-[10px] text-blue-600 font-medium'>
                        {t('filtry.wybrano', { liczba: selIpRatings.length })}
                      </span>
                    )}
                    <FontAwesomeIcon
                      icon={openAdvancedGroup === 'magIp' ? faChevronUp : faChevronDown}
                      className='text-[10px] text-gray-300 flex-shrink-0'
                    />
                  </span>
                </button>
                {openAdvancedGroup === 'magIp' && (
                  <div className='mt-1 px-1 pb-3 space-y-2'>
                    {MAG_IP_OPTIONS.map(ip => (
                      <label key={ip} className='flex items-center gap-2 cursor-pointer group'>
                        <StyledCheckbox
                          checked={selIpRatings.includes(ip)}
                          onChange={e => setSelIpRatings(prev => (
                            e.target.checked ? [...prev, ip] : prev.filter(v => v !== ip)
                          ))}
                        />
                        <span className='text-sm text-gray-600 group-hover:text-gray-800'>
                          {ip}
                          {(ip === 'IP20' || ip === 'IP21') && (
                            <span className='text-[10px] text-gray-400 ml-1'>{t('filtry.tylko_do_wnetrz')}</span>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )
    }

    if (productType !== 'panel') return null

    const hasPartialSellerFilter =
      selectedSellers.length > 0 && selectedSellers.length < allSellerKeys.length

    const hasAnyAdvanced =
      hasPartialSellerFilter ||
      selLengthPreset !== null ||
      selWidthRange !== null || selThicknessRange !== null || selMaxWeight !== null ||
      selFrameColour !== null ||
      selCells.length > 0 || selGamma !== null || selBeta !== null ||
      ELEC_RANGES_PANEL.some(r => elecRanges[r.key]?.min !== '' || elecRanges[r.key]?.max !== '')

    return (
      <div className='mt-2'>
        {/* Section toggle */}
        <div
          className='flex justify-between items-center cursor-pointer rounded-md px-2 py-2 hover:bg-gray-50 transition-colors border-t border-gray-100 pt-4'
          onClick={() => setShowAdvanced(p => !p)}
        >
          <span className='text-xs font-bold text-gray-500 uppercase tracking-wider'>
            {t('filtry.zaawansowane')}
            {hasAnyAdvanced && (
              <span className='ml-2 inline-flex items-center justify-center w-4 h-4 bg-blue-600 text-white rounded-full text-[9px]'>
                ✓
              </span>
            )}
          </span>
          <FontAwesomeIcon
            icon={showAdvanced ? faChevronUp : faChevronDown}
            className='text-gray-300 text-xs'
          />
        </div>

        {showAdvanced && (
          <div className='mt-2 pt-1 pb-2'>
            <p className='text-xs font-bold text-gray-700 mb-2 px-1'>{t('filtry.sekcja_panele')}</p>

            {renderSellerFilter()}

            <CollapsiblePresetGroup
              groupKey='length'
              label={t('filtry.dlugosc_modulu')}
              selected={selLengthPreset}
              onSelect={setSelLengthPreset}
              isOpen={openAdvancedGroup === 'length'}
              onToggle={toggleAdvancedGroup}
              options={ADVANCED_LENGTH_OPTS}
            />

            <CollapsiblePresetGroup
              groupKey='width'
              label={t('filtry.szerokosc_modulu')}
              selected={selWidthRange}
              onSelect={setSelWidthRange}
              isOpen={openAdvancedGroup === 'width'}
              onToggle={toggleAdvancedGroup}
              options={ADVANCED_WIDTH_OPTS}
            />

            <CollapsiblePresetGroup
              groupKey='thickness'
              label={t('filtry.grubosc_modulu')}
              selected={selThicknessRange}
              onSelect={setSelThicknessRange}
              isOpen={openAdvancedGroup === 'thickness'}
              onToggle={toggleAdvancedGroup}
              options={ADVANCED_THICKNESS_OPTS}
            />

            <CollapsiblePresetGroup
              groupKey='weight'
              label={t('filtry.ciezar_modulu')}
              selected={selMaxWeight}
              onSelect={setSelMaxWeight}
              isOpen={openAdvancedGroup === 'weight'}
              onToggle={toggleAdvancedGroup}
              options={ADVANCED_WEIGHT_OPTS}
            />

            <CollapsiblePresetGroup
              groupKey='frame'
              label={t('filtry.obramowanie')}
              selected={selFrameColour}
              onSelect={setSelFrameColour}
              isOpen={openAdvancedGroup === 'frame'}
              onToggle={toggleAdvancedGroup}
              options={ADVANCED_FRAME_OPTS}
            />

            {renderElectricalHeader()}
            {renderElectricalRanges(ELEC_RANGES_PANEL)}

            <CollapsibleMultiGroup
              groupKey='cells'
              label={t('filtry.liczba_ogniw')}
              options={CELLS_OPTIONS}
              selected={selCells}
              onChange={setSelCells}
              isOpen={openAdvancedGroup === 'cells'}
              onToggle={toggleAdvancedGroup}
            />

            <CollapsiblePresetGroup
              groupKey='gamma'
              label={t('filtry.wsp_temp_mocy')}
              selected={selGamma}
              onSelect={setSelGamma}
              isOpen={openAdvancedGroup === 'gamma'}
              onToggle={toggleAdvancedGroup}
              options={ADVANCED_GAMMA_OPTS(t)}
            />

            <CollapsiblePresetGroup
              groupKey='beta'
              label={t('filtry.wsp_temp_napiecia')}
              selected={selBeta}
              onSelect={setSelBeta}
              isOpen={openAdvancedGroup === 'beta'}
              onToggle={toggleAdvancedGroup}
              options={ADVANCED_BETA_OPTS(t)}
            />
          </div>
        )}
      </div>
    )
  }

  // Sprawność, gwarancja produktowa i cena za Wp — wyjęte z sekcji zaawansowanej
  // do głównej: to trzy filtry, po które użytkownicy sięgają najczęściej,
  // a były schowane za dodatkowym kliknięciem.
  // Bez wspólnego opakowania: każdy z tych filtrów niesie teraz własną ramkę
  // i własny odstęp, tak samo jak Producent i Rodzaj.
  const renderPanelMainPresets = () => (
    <>
      <CollapsiblePresetGroup
        groupKey='efficiency'
        label={t('filtry.sprawnosc')}
        wariant='glowny'
        selected={selEfficiency}
        onSelect={setSelEfficiency}
        isOpen={openAdvancedGroup === 'efficiency'}
        onToggle={toggleAdvancedGroup}
        options={ADVANCED_EFFICIENCY_OPTS}
      />
      <CollapsiblePresetGroup
        groupKey='warranty'
        label={t('filtry.gwarancja_produktowa')}
        wariant='glowny'
        selected={selWarranty}
        onSelect={setSelWarranty}
        isOpen={openAdvancedGroup === 'warranty'}
        onToggle={toggleAdvancedGroup}
        options={ADVANCED_WARRANTY_OPTS}
      />
      <CollapsiblePresetGroup
        groupKey='priceWp'
        label={t('filtry.cena_za_wp')}
        wariant='glowny'
        selected={selMaxPricePerWp}
        onSelect={setSelMaxPricePerWp}
        isOpen={openAdvancedGroup === 'priceWp'}
        onToggle={toggleAdvancedGroup}
        options={ADVANCED_PRICE_WP_OPTS}
      />
    </>
  )

  // ====== MAIN RENDER ======
  return (
    <section className='bg-white border border-gray-100 rounded-xl shadow-sm ml-4 sticky top-20 max-h-[calc(100vh-5.5rem)] flex flex-col w-72'>
      <div className='overflow-y-auto flex-1 overscroll-contain px-5 pt-5 pb-6'>
        {/* Clear button */}
        {Object.keys(filters).length > 0 && (
          <div className='flex justify-center mb-5 -mx-5 -mt-5 py-3 border-b border-gray-100 bg-gray-50'>
            <button
              onClick={clearFilters}
              className='text-xs text-gray-400 hover:text-red-500 hover:underline transition-colors'
            >
              {t('filtry.wyczysc')}
            </button>
          </div>
        )}

        {renderPriceFilter()}
        {productType === 'inwerter' && renderTypeButtonsFilter()}
        {(productType === 'panel' || productType === 'inwerter') && renderPowerFilter()}
        {productType === 'magazyn' && renderCapacityFilter()}
        {renderCheckboxFilters()}
        {productType === 'panel' && renderPanelMainPresets()}
        {productType === 'magazyn' && renderMagazineMainPresets()}
        {renderAvailabilityFilter()}
        {renderAdvancedFilters()}

        {/* Error message */}
        {errorMessage && !errorField && (
          <p className='text-red-500 text-xs mt-3 text-center'>{errorMessage}</p>
        )}
      </div>

      {/* Apply + Reset — pinned at bottom of panel */}
      <div className='flex-shrink-0 px-5 pb-5 pt-3 border-t border-gray-100 bg-white rounded-b-xl'>
        <button
          type='button'
          onClick={clearFilters}
          className='w-full mb-2 text-xs font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-lg px-4 py-2 transition-colors'
        >
          {t('filtry.resetuj')}
        </button>
        <button
          onClick={applyFilters}
          disabled={isFiltering}
          className='w-full bg-blue-600 hover:bg-blue-500 active:bg-blue-700 disabled:bg-blue-300 text-white text-sm font-semibold rounded-lg px-4 py-2.5 transition-colors shadow-sm'
        >
          {isFiltering
            ? t('filtry.filtrowanie_trwa')
            : activeFilterCount > 0
              ? t('filtry.zastosuj_z_liczba', { liczba: activeFilterCount })
              : t('filtry.zastosuj')}
        </button>
      </div>
    </section>
  )
}

export default Filters
